import { BASE_DOMAIN } from 'src/utils/config/domainConfig';
import { ONB_THEMES } from 'src/utils/constants';
import { getOnboardingUITheme } from 'src/utils/storage/helper';

/**
 * Determines the domain type based on the current host.
 * - localhost:3001 or groww.in → 'groww'
 * - localhost:3002 or 915.groww.in → '915'
 */
export type DomainTheme = 'groww' | '915';

export function getDomainBase(): DomainTheme {
  if (typeof window === 'undefined') {
    return 'groww';
  }
  const { hostname } = window.location;

  // Production / UAT domains
  if (hostname.includes(BASE_DOMAIN.NINE15)) {
    return '915';
  }

  // Local dev: port-based detection
  if (hostname === 'localhost') {

    return 'groww'; // port 3001 or any other
  }

  return 'groww';
}


/**
 * Sets `data-domain` and (for Groww) `data-theme` attributes on <html>,
 * then dynamically loads the matching theme CSS file.
 *
 * Must be called once at app startup, **before** React renders.
 *
 * Theme behaviour:
 *  - Groww  → supports light / dark (read from localStorage via getOnboardingUITheme)
 *  - 915    → single colour set, no light / dark
 */
export async function loadDomainTheme(): Promise<void> {
  const domain = getDomainBase();

  // Set domain attribute so CSS selectors `:root[data-domain="..."]` activate
  document.documentElement.setAttribute('data-domain', domain);

  if (domain === BASE_DOMAIN.NINE15) {
    // 915 has no light/dark – load single theme
    await import('./915Theme.css');
  } else {
    // Groww – resolve light/dark from storage (defaults to light)
    const storedTheme = getOnboardingUITheme();
    const colorScheme = storedTheme === ONB_THEMES.DARK ? ONB_THEMES.DARK : ONB_THEMES.LIGHT;

    document.documentElement.setAttribute('data-theme', colorScheme);
    document.documentElement.style.colorScheme = colorScheme;

    await import('./growwTheme.css');
  }
}

export function getDomainTheme() {
  const domain = getDomainBase();
  if (domain === BASE_DOMAIN.NINE15) {
    return ONB_THEMES.DARK;
  } else if (domain === 'groww') {
    const storedTheme = getOnboardingUITheme();
    return storedTheme === ONB_THEMES.DARK ? ONB_THEMES.DARK : ONB_THEMES.LIGHT;
  }
  return ONB_THEMES.LIGHT;
}
